import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter
} from '@angular/core';
import { IonicModule } from '@ionic/angular';

import { Capacitor } from '@capacitor/core';
import { CapacitorBarcodeScanner } from '@capacitor/barcode-scanner';
import { signal } from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromBottle from 'app/state/bottle';
import * as fromCan from 'app/state/can';
import * as fromUser from 'app/state/user';
import { OpenFoodFactsExternalService } from '../../services/external/open-food-facts.service';
import { OFFProduct } from '../../models/open-food-facts';
import * as fromBeverage from 'app/state/beverage';
import { ToastService } from 'app/services/toast.service';
import * as fromExternalApiSettings from 'app/state/external-api-settings';
import { firstValueFrom } from 'rxjs';
import { U } from 'vitest/dist/chunks/environment.LoooBwUu';
import { UntappdExternalService } from 'app/services/external/untappdExternal.service';
import { IUntappdBeer } from 'app/models/untappdExternalItem';

@Component({
  selector: 'app-barcode-scanner',
  templateUrl: './barcode-scanner.component.html',
  styleUrls: ['./barcode-scanner.component.scss'],
  imports: [IonicModule],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BarcodeScannerComponent {
  @Input() addingInventory: boolean = true;
  @Input() displayResult: boolean = false;

  @Output() handleProduct = new EventEmitter<OFFProduct>();
  @Output() handleUntappdProduct = new EventEmitter<IUntappdBeer>();
  @Output() barcodeScanned = new EventEmitter<string>();

  isScanning = signal<boolean>(false);
  scanError = signal<string | null>(null);
  scanResult = signal<string | null>(null);
  isLoading = signal<boolean>(false);

  isNativeApp = false;


  bottles = this.store.selectSignal(fromBottle.selectAllItems);
  cans = this.store.selectSignal(fromCan.selectAllItems);
  user = this.store.selectSignal(fromUser.selectUser);
  beverages = this.store.selectSignal(fromBeverage.selectAllItems);
  untappd = this.store.selectSignal(fromExternalApiSettings.selectUntappd);

  constructor(
    private store: Store,
    private toastService: ToastService,
    private untappdExternalService: UntappdExternalService,
    private openFoodFactsExternalService: OpenFoodFactsExternalService
  ) {
    this.isNativeApp = Capacitor.isNativePlatform();
  }

  async startScanning() {

    if (this.isScanning()) {
      return;
    }

    try {
      this.isScanning.set(true);
      this.isLoading.set(true);
      const result = await CapacitorBarcodeScanner.scanBarcode({
        hint: 17 //all
      });


      if (result && result.ScanResult) {
        if (this.addingInventory) {
          const bottle = this.bottles().find(b => b.uniqueId === result.ScanResult);
          const can = this.cans().find(c => c.uniqueId === result.ScanResult);

          if (bottle) {
            this.store.dispatch(fromBeverage.setItemAndNavigate({ id: bottle.beverageId ?? '' }));
            this.store.dispatch(fromBottle.setItem({ id: bottle.id ?? '' }));
            this.barcodeScanned.emit(result.ScanResult);
            return;
          }
          else if (can) {
            this.store.dispatch(fromBeverage.setItemAndNavigate({ id: can.beverageId ?? '' }));
            this.store.dispatch(fromCan.setItem({ id: can.id ?? '' }));
            this.barcodeScanned.emit(result.ScanResult);
            return;
          } else {
            if (result.ScanResult.indexOf('http') < 0) {
              await this.getItemFromApi(result.ScanResult);
            } else {
              //check if untappd is configured
              if (this.untappd()) {
                if (result.ScanResult.indexOf('https://untappd.com') > -1) {
                  const id = result.ScanResult.split('/').pop(); //Get the last part of the URL

                  if (id && !isNaN(parseInt(id))) {
                    const beerDetailsResponse = await firstValueFrom(
                      this.untappdExternalService.getBeerInfo(
                        this.untappd()?.authToken ?? '',
                        parseInt(id)));
                    if (beerDetailsResponse?.response?.beer) {
                      this.handleUntappdProduct.emit(beerDetailsResponse.response.beer);
                      this.barcodeScanned.emit(result.ScanResult);
                    }

                  }
                }
              }
            }
            return;
          }
        } else {
          this.handleScannedValue(result.ScanResult);
        }
      }
    } catch (error: any) {
      // User cancelled the scan - this is normal
      if (error.message !== 'User cancelled' && error.message !== 'User cancelled scan.' && error.message.indexOf('process was cancelled') === -1) {
        const errorMessage = error?.message || 'Failed to scan barcode';
        this.scanError.set(errorMessage);
        console.error('Scanning error:', error);
      }
    } finally {
      this.isLoading.set(false);
      this.isScanning.set(false);
    }
  }

  handleBarcodeInput(event: any) {
    const value = event.target.value;
    if (value && value.trim()) {
      this.handleScannedValue(value);
      event.target.value = '';
    }
  }

  private handleScannedValue(value: string) {
    const trimmedValue = value.trim();
    this.scanResult.set(trimmedValue);
    this.barcodeScanned.emit(trimmedValue);
  }


  async getItemFromApi(barcode: string) {
    this.isLoading.set(true);
    const email = this.user()?.email || 'info@barhelper.app';
    const result = await this.openFoodFactsExternalService.getProduct(barcode, email);
    this.isLoading.set(false);
    if (!result || !result.product) {
      this.toastService.error(`No product found for barcode: ${barcode}`);
      return;
    }

    this.handleProduct.emit(result.product);


  }


}
